import React, { useRef } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface ImageUploadFieldProps {
  label: string;
  value?: string;
  onUpload: (file: File) => Promise<string>;
  onChange?: (url: string) => void;
  helperText?: string;
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label, value, onUpload, onChange, helperText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await onUpload(file);
      onChange?.(url);
    } catch {
      setError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography fontSize={13} fontWeight={600} mb={1}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {value && (
          <Box
            component="img"
            src={value}
            alt={label}
            sx={{ maxHeight: 72, maxWidth: 160, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 1, p: 0.5, bgcolor: '#fff' }}
          />
        )}
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {value ? 'Replace Image' : 'Upload Image'}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" hidden
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </Box>
      {helperText && <Typography fontSize={11} color="text.secondary" mt={0.5}>{helperText}</Typography>}
      {error && <Typography fontSize={11} color="error" mt={0.5}>{error}</Typography>}
    </Box>
  );
};
